/**
 * Tiny inline SVG icon set. Each icon is a 16×16 stroke icon, currentColor.
 * Adding a new one: keep the viewbox 24×24 (so paths can be borrowed from
 * Lucide / Feather), draw with `stroke="currentColor"` and `fill="none"`,
 * stroke-width 1.75. Render at any size via the `size` prop.
 *
 * No icon font, no SVG sprite — Vite tree-shakes the unused ones away.
 */

import type { ReactElement, SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function base(size: number, children: ReactElement, extra?: SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...extra}
    >
      {children}
    </svg>
  );
}

export function IconImage({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <rect x="3" y="4.5" width="18" height="15" rx="2.5" />
      <circle cx="8.5" cy="10" r="1.5" />
      <path d="m4 18 5.5-6 4 5L18 12l3 6" />
    </>,
    p,
  );
}

export function IconLink({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 1 0-6.4-6.4l-1.5 1.5" />
      <path d="M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 1 0 6.4 6.4l1.5-1.5" />
    </>,
    p,
  );
}

export function IconTrash({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <path d="M4 7h16" />
      <path d="M9 7V4.5h6V7" />
      <path d="M6 7v12.5a1.5 1.5 0 0 0 1.5 1.5h9A1.5 1.5 0 0 0 18 19.5V7" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
    </>,
    p,
  );
}

export function IconArrowLeft({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <path d="M15 5 8 12l7 7" />
    </>,
    p,
  );
}

export function IconPlus({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <path d="M12 5v14M5 12h14" />
    </>,
    p,
  );
}

export function IconCheck({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <path d="m5 12 5 5L20 7" />
    </>,
    p,
  );
}

export function IconSparkles({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <path d="M12 4v4M12 16v4M4 12h4M16 12h4M6.5 6.5l2.8 2.8M14.7 14.7l2.8 2.8M17.5 6.5l-2.8 2.8M9.3 14.7l-2.8 2.8" />
    </>,
    p,
  );
}

export function IconSparkle({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <path d="M12 4v4M12 16v4M4 12h4M16 12h4M6.5 6.5l2.8 2.8M14.7 14.7l2.8 2.8M6.5 17.5l2.8-2.8M14.7 9.3l2.8-2.8" />
    </>,
    p,
  );
}

export function IconShare({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <circle cx="6" cy="12" r="2.5" />
      <circle cx="18" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <path d="m8.2 10.8 7.6-3.6M8.2 13.2l7.6 3.6" />
    </>,
    p,
  );
}

export function IconCog({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.36.16.68.41.93.73a1.65 1.65 0 0 0 1.07.42H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </>,
    p,
  );
}

export function IconClose({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <path d="m6 6 12 12M18 6 6 18" />
    </>,
    p,
  );
}

export function IconCopy({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V6a2 2 0 0 1 2-2h9" />
    </>,
    p,
  );
}

export function IconChevronRight({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <path d="m9 6 6 6-6 6" />
    </>,
    p,
  );
}

export function IconChat({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <path d="M21 12a8 8 0 0 1-11.4 7.2L4 20.5l1.3-5A8 8 0 1 1 21 12z" />
    </>,
    p,
  );
}

export function IconHeart({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <path d="M12 20s-7-4.35-9.5-9A5 5 0 0 1 12 6a5 5 0 0 1 9.5 5C19 15.65 12 20 12 20z" />
    </>,
    p,
  );
}

export function IconX({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <path d="m6 6 12 12M18 6 6 18" />
    </>,
    p,
  );
}

export function IconStar({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <path d="m12 4 2.4 5 5.6.8-4 3.9 1 5.5L12 16.6 7 19.2l1-5.5L4 9.8l5.6-.8L12 4z" />
    </>,
    p,
  );
}

export function IconClock({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>,
    p,
  );
}

export function IconEye({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
    </>,
    p,
  );
}

export function IconEyeOff({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <path d="M3 3l18 18" />
      <path d="M10.6 5.1A11 11 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.4 4.2" />
      <path d="M6.3 6.3A17 17 0 0 0 2 12s3.5 7 10 7c1.4 0 2.6-.3 3.8-.7" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    </>,
    p,
  );
}

export function IconCalendar({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M16 3v4M8 3v4M3 10h18" />
    </>,
    p,
  );
}

export function IconUser({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>,
    p,
  );
}

export function IconArrowRight({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </>,
    p,
  );
}

export function IconSend({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <path d="M3 20.5 21 12 3 3.5l3 8.5-3 8.5z" />
      <path d="M6 12h15" />
    </>,
    p,
  );
}

export function IconFork({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <circle cx="6" cy="5" r="2.5" />
      <circle cx="18" cy="5" r="2.5" />
      <circle cx="12" cy="19" r="2.5" />
      <path d="M6 7.5V11a3 3 0 0 0 3 3h6a3 3 0 0 0 3-3V7.5M12 14v2.5" />
    </>,
    p,
  );
}

export function IconBell({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <path d="M6 8a6 6 0 1 1 12 0v3.5l1.5 3.5H4.5L6 11.5V8z" />
      <path d="M9.5 18a2.5 2.5 0 0 0 5 0" />
    </>,
    p,
  );
}

export function IconBellOff({ size = 16, ...p }: IconProps) {
  return base(
    size,
    <>
      <path d="M6 8a6 6 0 0 1 9-5.2M18 11.5V8a6 6 0 0 0-.6-2.6" />
      <path d="M19.5 15H4.5L6 11.5" />
      <path d="M9.5 18a2.5 2.5 0 0 0 5 0" />
      <path d="M3 3l18 18" />
    </>,
    p,
  );
}
